import React, { useState } from 'react';
import PropTypes from 'prop-types';
import copyToClipboard from 'copy-to-clipboard';
import classnames from 'classnames';

import capitalize from 'common/util/capitalize';

import useDispatch from 'client/hooks/useDispatch';

import SrBox from 'client/components/SrBox';
import SrText from 'client/components/SrText';
import SrDarkModal from 'client/components/SrDarkModal';
import SrModalContent from 'client/components/SrModalContent';
import SrFlex from 'client/components/SrFlex';
import SrErrorMessage from 'client/components/SrErrorMessage/SrErrorMessage';
import SrSimpleMessageModal from 'client/components/SrSimpleMessageModal/SrSimpleMessageModal';
import SrButton, {
  SR_BUTTON_THEME_ADOBE_GREEN,
  SR_BUTTON_THEME_ADOBE_GREY,
  SR_BUTTON_THEME_ADOBE_RED,
} from 'client/components/SrButton/SrButton';
import UPAddressResolutionForm from 'client/components/UPAddressResolutionForm/UPAddressResolutionForm';
import UPAddressSearchForm from 'client/components/UPAddressSearchForm/UPAddressSearchForm';

import useAddressResolutionController from './useAddressResolutionController';

import './UPAddressResolutionDetailsModal.less';

const TAB_EXISTING = 'existing';
const TAB_NEW = 'new';
const TAB_CONFIG = {
  [TAB_EXISTING]: {
    value: TAB_EXISTING,
    inactiveText: 'Link to Existing Address',
    activeText: 'Linking to Existing Address',
  },
  [TAB_NEW]: {
    value: TAB_NEW,
    inactiveText: 'Link to New Address',
    activeText: 'Linking to New Address',
  },
};

const UPAddressResolutionDetailsModal = ({ item, onClose, reloadItems }) => {
  const [copied, setCopied] = useState(false);
  const [activeTab, setActiveTab] = useState(TAB_EXISTING);
  const dispatch = useDispatch();

  const {
    state: { isSubmitting, errorMessage, showIgnoreConfirmModal, successModalType },
    handlers: {
      handleExistingAddressSelect,
      handleNewAddressRegister,
      handleCloseSuccessModal,
      handleOpenIgnoreConfirm,
      handleCloseIgnoreConfirm,
      handleConfirmIgnore,
    },
  } = useAddressResolutionController({ item, reloadItems });

  const handleCopySearchTerm = (e) => {
    e.stopPropagation();
    copyToClipboard(item.searchTerm);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!item) {
    return null;
  }

  return (
    <>
      <SrDarkModal onClose={onClose}>
        <SrModalContent width={550} innerStyle={{ padding: 0 }}>
          <SrBox styleName="UPAddressResolutionDetailsModal">
            <SrBox styleName="modalHeader">
              <SrText
                font="greycliff"
                weight="800"
                lineHeight="25px"
                fontSize="25px"
                styleName="modalTitle"
              >
                Link Search Term to an Address
              </SrText>
            </SrBox>
            <SrBox padding="25px 40px 0px 40px">
              <SrBox flex="1">
                <SrFlex direction="row" gap="8px" justify="between" marginBottom="20px">
                  <SrBox gap="4px">
                    <SrText
                      font="greycliff"
                      weight="700"
                      fontSize="14px"
                      color="grey300"
                      styleName="fieldLabel"
                    >
                      Partner
                    </SrText>
                    <SrText font="greycliff" weight="600" fontSize="16px">
                      {item.partnerName}
                    </SrText>
                  </SrBox>
                  <SrBox gap="4px">
                    <SrText
                      font="greycliff"
                      weight="700"
                      fontSize="14px"
                      color="grey300"
                      styleName="fieldLabel"
                    >
                      Source
                    </SrText>
                    <SrText font="greycliff" weight="600" fontSize="16px">
                      {capitalize(item.source)}
                    </SrText>
                  </SrBox>
                </SrFlex>
                <SrBox marginBottom="24px">
                  <SrText
                    font="greycliff"
                    weight="700"
                    fontSize="14px"
                    color="grey300"
                    styleName="fieldLabel fieldLabelSpaced"
                  >
                    Address / Search Term
                  </SrText>
                  <SrFlex align="center" gap="8px">
                    <SrText font="greycliff" weight="600" fontSize="16px">
                      {item.searchTerm}
                    </SrText>
                    {copied ? (
                      <SrBox
                        onClick={handleCopySearchTerm}
                        backgroundImage='url("/images/checkmark-small-green.svg")'
                        styleName="copyIcon"
                        title={copied ? 'Copied!' : 'Copy to clipboard'}
                      />
                    ) : (
                      <SrBox
                        onClick={handleCopySearchTerm}
                        backgroundImage='url("/images/propertySetupDocs/copy-icon.svg")'
                        styleName="copyIcon"
                        title={copied ? 'Copied!' : 'Copy to clipboard'}
                      />
                    )}
                  </SrFlex>
                </SrBox>
              </SrBox>
            </SrBox>

            {/* Error message */}
            {errorMessage && (
              <SrBox padding="0 40px" marginBottom="16px">
                <SrErrorMessage>{errorMessage}</SrErrorMessage>
              </SrBox>
            )}

            {/* Form section */}
            <SrBox padding="0 40px 40px">
              <SrFlex direction="row" marginTop="24px">
                <SrBox
                  flex="1"
                  onClick={() => setActiveTab(TAB_EXISTING)}
                  styleName={classnames('tab', 'tabLeft', {
                    tabActive: activeTab === TAB_EXISTING,
                    tabInactive: activeTab !== TAB_EXISTING,
                  })}
                >
                  <SrText
                    font="greycliff"
                    weight="700"
                    fontSize="14px"
                    color={activeTab === TAB_EXISTING ? 'white' : 'grey300'}
                  >
                    {activeTab === TAB_EXISTING
                      ? TAB_CONFIG[TAB_EXISTING].activeText
                      : TAB_CONFIG[TAB_EXISTING].inactiveText}
                  </SrText>
                </SrBox>
                <SrBox
                  flex="1"
                  onClick={() => setActiveTab(TAB_NEW)}
                  styleName={classnames('tab', 'tabRight', {
                    tabActive: activeTab === TAB_NEW,
                    tabInactive: activeTab !== TAB_NEW,
                  })}
                >
                  <SrText
                    font="greycliff"
                    weight="700"
                    fontSize="14px"
                    color={activeTab === TAB_NEW ? 'white' : 'grey300'}
                  >
                    {activeTab === TAB_NEW
                      ? TAB_CONFIG[TAB_NEW].activeText
                      : TAB_CONFIG[TAB_NEW].inactiveText}
                  </SrText>
                </SrBox>
              </SrFlex>

              {activeTab === TAB_EXISTING && (
                <SrBox styleName="tabContent">
                  <UPAddressSearchForm
                    dispatch={dispatch}
                    onSubmit={handleExistingAddressSelect}
                    buttonText="Link to Address"
                    fieldTitle="Search Existing Address"
                    disabled={isSubmitting}
                  />
                </SrBox>
              )}

              {activeTab === TAB_NEW && (
                <SrBox styleName="tabContent">
                  <UPAddressResolutionForm
                    dispatch={dispatch}
                    utilityPartnerId={item.partnerId}
                    disabled={isSubmitting}
                    onRegister={handleNewAddressRegister}
                  />
                </SrBox>
              )}

              <SrFlex justify="end" marginTop="16px">
                <SrButton
                  theme={SR_BUTTON_THEME_ADOBE_RED}
                  onClick={handleOpenIgnoreConfirm}
                  disabled={isSubmitting}
                >
                  Ignore Search Term
                </SrButton>
              </SrFlex>
            </SrBox>
          </SrBox>
        </SrModalContent>
      </SrDarkModal>

      {showIgnoreConfirmModal && (
        <SrSimpleMessageModal
          iconUrl="/images/big-red-question-mark.png"
          headline="Ignore this search term?"
          underline
          onClose={handleCloseIgnoreConfirm}
          message={
            <SrBox maxWidth="320px" margin="0 auto">
              <SrText fontSize="16px" display="block">
                This will mark it as ignored and remove it from the queue.
              </SrText>
            </SrBox>
          }
          srMultiButtons={[
            {
              buttonText: 'Ignore Search Term',
              buttonTheme: SR_BUTTON_THEME_ADOBE_RED,
              onButtonClick: handleConfirmIgnore,
              buttonDisabled: isSubmitting,
            },
            {
              buttonText: 'Cancel',
              buttonTheme: SR_BUTTON_THEME_ADOBE_GREY,
              onButtonClick: handleCloseIgnoreConfirm,
              buttonDisabled: isSubmitting,
            },
          ]}
          maxWidth={400}
        />
      )}

      {successModalType && (
        <SrSimpleMessageModal
          iconUrl="/images/checkmark-circle-large-green.svg"
          headline="Success!"
          underline
          message={
            <SrBox maxWidth="280px" margin="0 auto">
              <SrText font="greycliff" weight="700" fontSize="16px" display="block">
                {item.searchTerm}
              </SrText>
              <SrText fontSize="16px" display="block">
                {successModalType === 'ignored'
                  ? 'Has been marked as ignored.'
                  : 'Has been linked to an address.'}
              </SrText>
            </SrBox>
          }
          srMultiButtons={[
            {
              buttonText: (
                <SrFlex align="center" justify="center">
                  Continue
                </SrFlex>
              ),
              buttonTheme: SR_BUTTON_THEME_ADOBE_GREEN,
              onButtonClick: () => {
                handleCloseSuccessModal();
                onClose();
              },
            },
          ]}
          maxWidth={360}
          maxIconHeight="100"
          maxIconWidth="100"
        />
      )}
    </>
  );
};

UPAddressResolutionDetailsModal.propTypes = {
  item: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  reloadItems: PropTypes.func,
};

UPAddressResolutionDetailsModal.displayName = 'UPAddressResolutionDetailsModal';

export default UPAddressResolutionDetailsModal;
